import { useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import {
  emptyRows,
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import { useGetRestaurantPlanGroups } from 'src/api/restaurant';
import { useAuthContext } from 'src/auth/hooks';
import { hasPermissions } from 'src/auth/guard/utils';
import { components } from 'src/types/schema';


const tableConfig = {
  id: 'plangroup',
  header: [
    { id: 'tag', label: 'Tag' },
    { id: 'specialName', label: 'Special Name' },
    { id: 'protein', label: 'Protein Size' },
    { id: 'carb', label: 'Carb Size' },
    { id: 'plans', label: 'Plans' },
    { id: 'status', label: 'Status' },
    { id: 'action', label: '' },
  ],
  data: [],
};

export default function RestaurantPlangroupsList({ id }: { id: string }) {
  // Retrieve initial filters and pagination from localStorage
  const initialPagination = JSON.parse(
    localStorage.getItem('restaurantPlangroupPagination') || '{"page": 0, "rowsPerPage": 25}'
  );

  const table = useTable({
    defaultRowsPerPage: initialPagination.rowsPerPage,
    defaultCurrentPage: initialPagination.page,
  });

  const { user } = useAuthContext();

  const { restaurantPlanGroups, restaurantPlanGroupsLoading } = useGetRestaurantPlanGroups(id, '');

  const router = useRouter();

  const [tableData, setTableData] =
    useState<components['schemas']['PartnersPlanGroup'][]>(restaurantPlanGroups);

  useEffect(() => {
    if (!restaurantPlanGroupsLoading && restaurantPlanGroups) {
      setTableData(restaurantPlanGroups);
    }
  }, [restaurantPlanGroups, restaurantPlanGroupsLoading]);

  const denseHeight = table.dense ? 52 : 72;

  const notFound = !id;

  const canUpdatePlanGroups = hasPermissions(user, ['partners:update:plangroups']);

  const handleEditRow = useCallback(
    (uid: string) => {
      if (canUpdatePlanGroups) {
        router.push(paths.dashboard.restaurant.plangroup.edit(id, uid));
      }
    },
    [canUpdatePlanGroups, router, id]
  );

  return (
    <Container maxWidth={false}>
      <Card>
        <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={table.order}
                orderBy={table.orderBy}
                headLabel={tableConfig.header}
                rowCount={tableData.length}
                numSelected={table.selected.length}
                onSort={table.onSort}
              />

              <TableBody>
                {restaurantPlanGroupsLoading
                  ? [...Array(table.rowsPerPage)].map((i, index) => (
                      <TableSkeleton key={index} sx={{ height: denseHeight }} />
                    ))
                  : tableData.map((row) => (
                      <PlangroupTableRow
                        key={row.id}
                        row={row}
                        onEditRow={() => handleEditRow(row.id.toString())}
                      />
                    ))}

                <TableEmptyRows
                  height={denseHeight}
                  emptyRows={emptyRows(table.page, table.rowsPerPage, tableData.length)}
                />

                <TableNoData notFound={notFound} />
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={tableData.length}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}

type PlangroupRowProps = {
  row: components['schemas']['PartnersPlanGroup'];
  onEditRow?: VoidFunction;
};

function PlangroupTableRow({ row, onEditRow }: PlangroupRowProps) {
  return (
    <TableRow hover>
      <TableCell>
        <Typography
          onClick={onEditRow}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          variant="subtitle2"
          noWrap
        >
          {row.plan_tag.name_en}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2" noWrap>
          {row.special_name_en || '-'}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2" noWrap>
          {row.protein_size || 0}g
        </Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2" noWrap>
          {row.carb_size || 0}g
        </Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2" noWrap>
          {row.plans.length || 0}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2" noWrap>
          {row.status}
        </Typography>
      </TableCell>

      <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
        <IconButton color="default" onClick={onEditRow}>
          <Iconify icon="solar:pen-bold" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
